import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LoadingContext } from "../../../../contexts/Loading";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { REGISTER } from "../../../../enums/ApiPaths";
import { CLINICS } from "../../../../enums/QueryKeys";
import { HOME, RESEND_VERIFICATION_EMAIL } from "../../../../enums/UrlPaths";
import authApi from "../../../../utils/api/authApi";
import { getBearerToken } from "../../../../utils/Functions";
import {
  signUpValueParser,
  getSignUpResolver,
} from "../../utils/AuthZodValidation";
import UserRegBody from "../form/UserRegBody";
import { fetchClinicList } from "../../../../enums/queryFunction/Fetch";
import { useLanguageAwareForm } from "../../../../hooks/useLanguageAwareForm";

// Custom hook to create a Memoized Zod Resolver that updates on language change
// We do this because the validation parser depends on i18n t object to return translated messages.
const usePersonalRegResolver = () => {
  const { t } = useTranslation();
  const schema = React.useMemo(() => {
    return getSignUpResolver(t);
  }, [t]);

  return React.useMemo(() => {
    return zodResolver(schema);
  }, [schema]);
};
const SIGN_UP_PERSONAL_SERVER = "signUpPersonalServer"; 

export default function UserReg({ activeStep, getPageErrors }) {
  const { data } = useQuery({
    queryKey: [CLINICS],
    queryFn: fetchClinicList,
    staleTime: Infinity,
  });
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const resolver = usePersonalRegResolver();

  const methods = useLanguageAwareForm({
    resolver: resolver,
    mode: "onSubmit",
  });

  const { errors, setError } = methods.formState;
 
  React.useEffect(() => {
    getPageErrors(errors);
  }, [errors, getPageErrors]);

  React.useEffect(() => {
    async function fetch() {
      const isToken = await getBearerToken();
      if (isToken) {
        navigate(HOME);
      }
    }

    fetch();
  });


  const registrationSubmit = async (e) => {
    const data = signUpValueParser(e);
    try {
      setIsLoading(true);
      await authApi.post(REGISTER, data);
      handleSnackbar("AccountCreated", "success");
      navigate(RESEND_VERIFICATION_EMAIL, {
        state: { email: data?.email },
      });
    } catch (error) {
      const errorResponse = error?.response?.data;
      if (typeof errorResponse === "object" && errorResponse !== null) {
        Object.entries(errorResponse).forEach(([field, message]) => {
          setError(field, {
            type: SIGN_UP_PERSONAL_SERVER,
            message: message,
          });
        });
      }
      handleSnackbar(error?.response?.data || t("UnknownError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(registrationSubmit)}>
        <UserRegBody
          activeStep={activeStep}
          isLoading={isLoading}
          data={data}
        />
      </form>
    </FormProvider>
  );
}
